import {
  AaaContact,
  AaaServiceLocation,
  FoiType,
  AaaAddressType,
  AaaContactType,
  AaaPhoneType,
  AaaAddress,
  LocationCode,
  StandardServiceCall,
  AaaCommentType,
  AaaTroubleCode,
  PriortyCode,
  PrunedAAADispatch,
} from 'types/aaa'
import {
  Branch,
  Brands,
  EitherOrBoth,
  Participant,
  PartialVehicle,
  TroubleCode,
  TroubleCodeType,
  ParticipantAddress,
  YesNo,
} from 'types/global'
import { GeocodeAddress } from 'types/googleMaps'
import { SoftService, Tow, TowExchange } from 'types/callstore'
import { isBranch, isLocation, isParticipant, isNumeric } from 'utils/typeChecks'
import { BreakdownOptions } from 'forms/CallForm/BreakdownDetails/BreakdownRadio'
import { isEmpty, isNil, isArray } from 'lodash'
import { LocationPayload, ServiceLocationType } from 'types/location'
import { formatHours } from 'utils/formatHours'

const TroubleCodeDescriptions = {
  [TroubleCode.T1]: 'Flat w/Spare',
  [TroubleCode.T2]: 'Flat No Spare',
  [TroubleCode.T3]: 'Battery',
  [TroubleCode.T4]: "Can't Start",
  [TroubleCode.T5]: 'Fuel',
  [TroubleCode.T6]: 'Tow',
  [TroubleCode.T7]: 'Lockout',
  [TroubleCode.T8]: 'Extrication (Winch)',
  [TroubleCode.T9]: 'Miscellaneous',
  [TroubleCode.T0]: 'Gone on arrival',
  [TroubleCode.A7]: 'Lockout - Key Delivery',
  [TroubleCode.B7]: 'Lockout - Locksmith',
}
const PriorityCodeDescription = {
  P1: 'P1 - BABY/ANIMAL LOCKED IN CAR',
  P2: 'P2 - URGENT SAFETY ISSUE',
  P3: 'P3 - EXTREME WEATHER/NO SHELTER',
  P4: 'P4 - PUBLIC AGENCY/POLICE ON SCENE',
  P5: 'P5 - BLOCKING TRAFFIC',
  P6: 'P6 - ACCIDENT',
  P7: 'P7 - EXPRESSWAY CALL',
  P8: 'P8 - LOCKOUT CAR RUNNING',
  R1: 'R1 - AAA EMPLOYEE/VIP',
  R2: 'R2 - AAA ERROR/RE-DISPATCH CALL',
}

export type LocationPayloadParticipant = EitherOrBoth<LocationPayload, Participant>

const PHOTOS_REQUIRED_COMMENT = 'PHOTOS REQUIRED'

export const createSoftService = (standard: StandardServiceCall, softService: SoftService, troubleCode: TroubleCode) => {
  let request = standard

  // Add Vehicle

  request = addVehicle(request, softService.vehicle as PartialVehicle)

  // Add Breakdown Location
  if (softService.serviceLocations.vehicleLocation)
    request = addServiceLocation(request, softService.serviceLocations.vehicleLocation)

  // Add Comments
  const { breakdownOption } = softService
  if (breakdownOption === BreakdownOptions.ROADWAYINTERSTATE || breakdownOption === BreakdownOptions.PARKINGGARAGE) {
    request = addComment(request, breakdownOption)
  }

  if (breakdownOption === BreakdownOptions.ROADWAYINTERSTATE) {
    request = addComment(request, PriorityCodeDescription.P7)
  }

  request = addSplitComments(request, softService.notes)

  // Add Priority Code
  if (breakdownOption === BreakdownOptions.ROADWAYINTERSTATE) {
    request = addPriorityCode(request, PriortyCode.P7)
  }

  // Add Location Codes
  if (breakdownOption === BreakdownOptions.ROADWAYINTERSTATE) {
    request = addLocationCode(request, LocationCode.RD)
  }
  if (softService.breakdownOption === BreakdownOptions.PARKINGGARAGE) {
    request = addLocationCode(request, LocationCode.PS)
  }

  const serviceCode = { code: '', description: '', troubleCodeType: '' }
  serviceCode.code = troubleCode
  serviceCode.description = TroubleCodeDescriptions[troubleCode]
  serviceCode.troubleCodeType = TroubleCodeType.PROBLEM

  request = addService(request, serviceCode)
  return request
}

export const createTowService = (standard: StandardServiceCall, tow: Tow | TowExchange, troubleCode: TroubleCode) => {
  const formattedBusinessHours = formatHours(tow.serviceLocations.vehicleDestinationLocation as unknown as Participant)

  let request = standard
  let locationValues = Object.values(tow.serviceLocations)

  // Add Vehicle
  request = addVehicle(request, tow.vehicle as PartialVehicle, tow.vehicleCondition)

  // Add Service Locations
  request = addServiceLocation(request, locationValues, tow)

  const { breakdownOption } = tow

  // Add Comments
  if (breakdownOption === BreakdownOptions.ROADWAYINTERSTATE || breakdownOption === BreakdownOptions.PARKINGGARAGE)
    request = addComment(request, breakdownOption)

  request = addSplitComments(request, tow.notes)
  request = addComment(request, PHOTOS_REQUIRED_COMMENT)
  if (tow.vehicleCondition === 'Accident/Collision/Vandalism Tow') request = addComment(request, PriorityCodeDescription.P6)
  if (tow.breakdownOption === BreakdownOptions.ROADWAYINTERSTATE) request = addComment(request, PriorityCodeDescription.P7)

  const { serviceLocations } = tow
  // Change program code (aka org code) for Tow Exchange with delivery
  // Deliver New Vehicle - Tow Disabled vehicle AND Deliver New Vehicle
  if ((tow as TowExchange).towDirection === 'branch' && serviceLocations?.exchangeLocation) {
    const isVehicleDestParticipant =
      !isNil(serviceLocations.vehicleDestinationLocation?.entity) && !isEmpty(serviceLocations.vehicleDestinationLocation?.entity)
    const vehicleDestLocName = isVehicleDestParticipant ? serviceLocations.vehicleDestinationLocation?.entity?.name || '' : ''
    const vehicleDestLocAddress = isVehicleDestParticipant
      ? serviceLocations.vehicleDestinationLocation?.entity?.address
      : serviceLocations.vehicleDestinationLocation?.address
    const vehicleDestLocAddressString = isVehicleDestParticipant
      ? `${vehicleDestLocName} - ${(vehicleDestLocAddress as ParticipantAddress)?.addressLine1}, ${(vehicleDestLocAddress as ParticipantAddress)?.city}, ${(vehicleDestLocAddress as ParticipantAddress)?.administrativeArea} ${(vehicleDestLocAddress as ParticipantAddress)?.postalCode}`
      : `${(vehicleDestLocAddress as LocationPayload)?.address.streetNumber} ${(vehicleDestLocAddress as LocationPayload)?.address.street}, ${(vehicleDestLocAddress as LocationPayload)?.address.city} ${(vehicleDestLocAddress as LocationPayload)?.address.state}, ${(vehicleDestLocAddress as LocationPayload)?.address.postalCode}`

    if ((tow as TowExchange).disabledVehiclePickup) {
      // Only add this comment for Deliver New Vehicle > Tow Disabled vehicle
      request = addSplitComments(request, `TOW EXCHANGE - P/U DISABLED VEHICLE AND TOW TO - ${vehicleDestLocAddressString}`)
    }

    const organization = {
      code: TowExchangeProgramCodes[request.customer.organization!.name as Brands],
      name: TowExchangeProgramNames[request.customer.organization!.name as Brands],
    }
    const customer = { ...request.customer, organization }

    request = { ...request, customer }
  }

  // Add Priority Codes
  if (tow.vehicleCondition === 'Accident/Collision/Vandalism Tow') request = addPriorityCode(request, PriortyCode.P6)
  if (tow.breakdownOption === BreakdownOptions.ROADWAYINTERSTATE) request = addPriorityCode(request, PriortyCode.P7)

  // Add Location Code
  if (tow.breakdownOption === BreakdownOptions.ROADWAYINTERSTATE) request = addLocationCode(request, LocationCode.RD)
  if (tow.breakdownOption === BreakdownOptions.PARKINGGARAGE) request = addLocationCode(request, LocationCode.PS)

  const serviceCode = { code: '', description: '', troubleCodeType: '' }
  serviceCode.code = troubleCode
  serviceCode.description = TroubleCodeDescriptions[troubleCode]
  serviceCode.troubleCodeType = TroubleCodeType.PROBLEM

  request = addService(request, serviceCode)

  request = addRideAlong(request, tow)
  request = addUnattended(request, tow)

  if (formattedBusinessHours !== '') request = addSplitComments(request, 'Destination Business Hours: ' + formattedBusinessHours)
  else request = addComment(request, 'Destination Business Hours Unavailable.')

  // Add after-hours drop instructions note (use N/A if instructions are not provided)
  const afterHoursDrop = tow.serviceLocations.vehicleDestinationLocation?.properties?.afterHoursDrop
  const afterHoursDropInstructions = tow.serviceLocations.vehicleDestinationLocation?.properties?.afterHoursDropInstructions
  if (afterHoursDrop?.toUpperCase() === YesNo.YES) {
    request = addSplitComments(request, `After Hours Drop Accepted, Drop Instructions: ${afterHoursDropInstructions ?? 'N/A'}`)
  }

  return request
}

export const addUnattended = (standard: StandardServiceCall, tow: Tow | TowExchange) => {
  if (tow.unattended) {
    const { vehicle, vehicleCondition, unattended, keysLocation, keysLocationOther } = tow
    return addVehicle(
      standard,
      {
        ...(vehicle as PartialVehicle),
        unattended,
        unattendedNotes: `KEYS: ${keysLocationOther || keysLocation!.label}`,
      },
      vehicleCondition,
    )
  }
  return standard
}

export const addRideAlong = (standard: StandardServiceCall, tow: Tow | TowExchange) => {
  let request = standard
  if (tow.rideAlong) {
    request = {
      ...request,
      service: {
        ...request.service,
        rideAlongRequested: true,
      },
    }

    if (tow.rideAlongLocation) {
      // strip html
      const html = tow.rideAlongLocation
      const address = html.replace(/<br\s*\/?>/gi, ', ')

      request = {
        ...request,
        service: {
          ...request.service,
          rideAlongNotes: address.substring(0, 180),
        },
      }
    }
  }

  return request
}

// Modify StandardServiceCall functions

export const addService = (request: StandardServiceCall, service: AaaTroubleCode): StandardServiceCall => ({
  ...request,
  service: {
    ...request.service,
    requestedServices: [...request.service.requestedServices, service],
  },
})

const formatAddress = (
  address: string,
  city: string,
  state: string,
  postalCode: string,
  country: string,
  streetNumber?: string,
): Partial<AaaAddress> => {
  let street: string = address
  postalCode = postalCode.split('-')[0]
  if (!streetNumber) {
    const [houseNumber, ...rest] = address && address.split(' ')
    if (isNumeric(houseNumber)) {
      streetNumber = houseNumber
      street = rest.join(' ')
    } else {
      street = address
    }
  }

  return {
    ...(isNumeric(streetNumber) && { streetNumber }),
    street,
    city,
    state,
    postalCode,
    country,
  }
}

export const locationToAAALocation = (
  location?: LocationPayloadParticipant | null,
  locationType?: 'RETAL_PICKUP' | 'TOW_DESTINATION' | 'BREAKDOWN',
) => {
  // Network Manager
  if (isParticipant(location)) {
    const participantLocation = location as Participant & LocationPayload
    const { addressLine1, city, administrativeArea, postalCode, country } = participantLocation.entity.address!
    const { latitude, longitude } = participantLocation.location!
    const { primaryPhone } = participantLocation.entity.contactInfo!

    return {
      serviceLocationType: locationType || ServiceLocationType.TOW_DESTINATION,
      foi: {
        foiType: FoiType.ADDRESS,
        name: participantLocation.entity.name as string | undefined,
        location: {
          address: {
            addressType: AaaAddressType.PHYSICAL,
            ...formatAddress(addressLine1!, city!, administrativeArea!, postalCode!, country!),
          },
          latitude,
          longitude,
        },
        ...(primaryPhone && {
          contacts: [
            {
              contactType: AaaContactType.FACILITY,
              phones: [
                {
                  phoneType: AaaPhoneType.BUSINESS,
                  phoneNumber: primaryPhone.replace(/\D/g, ''),
                },
              ],
            },
          ],
        }),
      },
    }
  }

  // Breakdown
  if (isLocation(location)) {
    const breakdownLocation = location as LocationPayload
    let extendedAddress = breakdownLocation.addressDescription

    return {
      serviceLocationType: locationType || ServiceLocationType.BREAKDOWN,
      foi: {
        name: breakdownLocation.serviceLocationType,
        foiType: FoiType.ADDRESS,
        location: {
          address: {
            addressType: AaaAddressType.PHYSICAL,
            extendedAddress,
            ...location.address,
          },
          latitude: breakdownLocation.latitude,
          longitude: breakdownLocation.longitude,
        },
      },
    }
  }

  // Rental Branch
  if (isBranch(location)) {
    const branchLocation = location as Branch
    const { street_addresses, city, postal, country_code, country_subdivision_code: state } = branchLocation.address
    return {
      serviceLocationType: locationType,
      foi: {
        foiType: FoiType.ADDRESS,
        name: branchLocation.brand,
        id: branchLocation.additional_data.group_branch_number,
        location: {
          address: {
            addressType: AaaAddressType.PHYSICAL,
            ...formatAddress(street_addresses[0], city, state, postal, country_code),
          },
          latitude: parseFloat(branchLocation.gps.latitude.toString()),
          longitude: parseFloat(branchLocation.gps.longitude.toString()),
        },
        contacts: [
          {
            contactType: AaaContactType.AGENT,
            phones: [
              {
                phoneType: AaaPhoneType.BUSINESS,
                phoneNumber: branchLocation.additional_data.formatted_phone.replace(/\D/g, '').substr(1),
              },
            ],
          },
        ],
      },
    }
  }
}

const handleAddSingleServiceLocation = (location: LocationPayloadParticipant, request: StandardServiceCall) => {
  const serviceLocations = request.service.serviceLocations as Array<AaaServiceLocation>

  // Assertion that this is a single object
  const loc = locationToAAALocation(location)
  if (loc) serviceLocations.push(loc)
  return request
}

const handleMultipleServiceLocation = (
  locationArray: (LocationPayloadParticipant | null)[],
  request: StandardServiceCall,
  tow: Tow | TowExchange,
) => {
  const towExchange = tow as TowExchange
  if (isNil(locationArray)) return request

  // AAA Service Locations
  const serviceLocations = request.service.serviceLocations as Array<AaaServiceLocation>

  const vehicleLocation = locationArray?.find((loc) => loc && loc.serviceLocationType === ServiceLocationType.VEHICLE)
  const callerLocation = locationArray?.find((loc) => loc && loc.serviceLocationType === ServiceLocationType.CALLER)
  const exchangeLocation = locationArray?.find((loc) => loc && loc.serviceLocationType === ServiceLocationType.EXCHANGE)
  const towDestLocation = locationArray?.find((loc) => {
    if (isNil(loc)) return undefined
    if (loc.serviceLocationType === ServiceLocationType.VEHICLEDESTINATION) return true
    if (loc.serviceLocationType === ServiceLocationType.TOW_DESTINATION) return true
    return undefined
  })

  // TOW => EXCHANGE
  if (towExchange.exchange) {
    // ***Customer Supplied***
    // customerSupplied = true && rideAlong = false && towDirection = disabledVehicle

    if (towExchange.customerSupplied && towExchange.towDirection === 'disabledVehicle') {
      const breakdownLocation = locationToAAALocation(vehicleLocation, 'BREAKDOWN')
      if (breakdownLocation) serviceLocations.push(breakdownLocation)

      const towDestinationLocation = locationToAAALocation(towDestLocation, 'TOW_DESTINATION')
      if (towDestinationLocation) serviceLocations.push(towDestinationLocation)
    }

    // ***Ride Along***
    // customerSupplied = null &&  rideAlong = true && towDirection = disabledVehicle
    if (tow.rideAlong && (tow as TowExchange).towDirection === 'disabledVehicle') {
      const breakdownLocation = locationToAAALocation(vehicleLocation, 'BREAKDOWN')
      if (breakdownLocation) serviceLocations.push(breakdownLocation)

      const towDestinationLocation = locationToAAALocation(towDestLocation, 'TOW_DESTINATION')
      if (towDestinationLocation) serviceLocations.push(towDestinationLocation)
    }

    // ***Deliver New Vehicle***
    // towDirection = branch && type = towExchange
    if (towExchange.towDirection === 'branch' && towExchange.type === 'towExchange') {
      // Is Delivery Only?
      if (towExchange.disabledVehiclePickup === false) {
        if (!isNil(exchangeLocation)) {
          const breakdownLocation = locationToAAALocation(exchangeLocation, 'BREAKDOWN')
          if (breakdownLocation) serviceLocations.push(breakdownLocation)

          const towDestinationLocation = locationToAAALocation(callerLocation, 'TOW_DESTINATION')
          if (towDestinationLocation) serviceLocations.push(towDestinationLocation)
        }
      } else {
        const isExchangeLocationPinned = !isNil(exchangeLocation)
        const isTowDestLocationPinned = !isNil(towDestLocation)
        const isCallerLocationPinned = !isNil(callerLocation)
        const isVehicleLocationPinned = !isNil(vehicleLocation)
        const isCallerAndVehicleSameLoc =
          isCallerLocationPinned && isVehicleLocationPinned
            ? callerLocation.latitude === vehicleLocation.latitude && callerLocation.longitude === vehicleLocation.longitude
            : false

        // All 4 locations different
        if (
          isExchangeLocationPinned &&
          isTowDestLocationPinned &&
          isCallerLocationPinned &&
          isVehicleLocationPinned &&
          isCallerAndVehicleSameLoc
        ) {
          const callerServiceLoc = locationToAAALocation(callerLocation)
          if (callerServiceLoc) serviceLocations.push(callerServiceLoc)

          const vehicleServiceLoc = locationToAAALocation(vehicleLocation)
          if (vehicleServiceLoc) serviceLocations.push(vehicleServiceLoc)

          const exchangeServiceLoc = locationToAAALocation(exchangeLocation)
          if (exchangeServiceLoc) serviceLocations.push(exchangeServiceLoc)

          const towDestServiceLoc = locationToAAALocation(towDestLocation)
          if (towDestServiceLoc) serviceLocations.push(towDestServiceLoc)
        } else {
          // Caller & Vehicle Locations the same
          if (isExchangeLocationPinned) {
            const breakdownLocation = locationToAAALocation(exchangeLocation, 'BREAKDOWN')
            if (breakdownLocation) serviceLocations.push(breakdownLocation)
          }

          const towDestinationLocation =
            isNil(callerLocation) && !isNil(vehicleLocation)
              ? locationToAAALocation(vehicleLocation, 'TOW_DESTINATION')
              : locationToAAALocation(callerLocation, 'TOW_DESTINATION')

          if (towDestinationLocation) serviceLocations.push(towDestinationLocation)
        }
      }
    }
  }

  // TOW ==> NO EXCHANGE
  if (!towExchange.exchange) {
    // Tow + No Exchange Locations
    const breakdownLocation = locationToAAALocation(vehicleLocation)
    if (breakdownLocation) serviceLocations.push(breakdownLocation)

    const towDestinationLocation = locationToAAALocation(towDestLocation)
    if (towDestinationLocation) serviceLocations.push(towDestinationLocation)
  }

  return request
}

export const addServiceLocation = (
  request: StandardServiceCall,
  location: LocationPayloadParticipant | null | (LocationPayloadParticipant | null)[],
  tow?: Tow | TowExchange,
): StandardServiceCall => {
  // Guard Clauses
  if (isNil(location) || isEmpty(location)) return request

  // Single Instance Guard Clause
  if (!isArray(location)) return handleAddSingleServiceLocation(location, request)

  // Array Instance Guard Clause
  if (isArray(location)) return handleMultipleServiceLocation(location, request, tow as TowExchange)

  return request
}

export const addSplitComments = (request: StandardServiceCall, comment: string) => {
  //split comments(including whitespace/escaped characters) into 240 character chunks taking whole words into account
  //regex uses the number 238 but this accounts for leading and trailing white space
  if (isNil(comment)) return { ...request }
  if (isEmpty(comment)) return { ...request }
  let splitComments = comment.match(/\S.(.|[\r\n]){0,238}(?<=\S)(?!\S)/g)

  //send each split comment as a separate comment in the payload
  splitComments?.forEach((comment) => {
    request = addComment(request, comment)
  })

  return request
}

export const addComment = (request: StandardServiceCall, comment: string): StandardServiceCall => {
  if (isNil(comment)) return { ...request }
  if (isEmpty(comment)) return { ...request }

  const newComment = { commentType: AaaCommentType.NORMAL, text: comment }
  const comments = [...request.comments, newComment]

  return { ...request, comments }
}

export const addPriorityCode = (request: StandardServiceCall, inputCode: string) => {
  if (request.service.priorityCode && request.service.priorityCode.substring(1, 2) < inputCode.substring(1, 2)) {
    const service = { ...request.service, priorityCode: request.service.priorityCode }
    return { ...request, service }
  } else {
    const service = { ...request.service, priorityCode: inputCode }
    return { ...request, service }
  }
}

export const addLocationCode = (request: StandardServiceCall, inputCode: LocationCode) => {
  let serviceLocation = request.service.serviceLocations[0]

  return {
    ...request,
    service: {
      ...request.service,
      serviceLocations: [
        {
          ...serviceLocation,
          foi: {
            ...serviceLocation.foi,
            location: {
              ...serviceLocation.foi.location,
              locationCode: inputCode,
            },
          },
        },
        ...request.service.serviceLocations.slice(1),
      ],
    },
  }
}

export const addCustomerContact = (request: StandardServiceCall, contact: AaaContact): StandardServiceCall => ({
  ...request,
  customer: {
    ...request.customer,
    contact,
  },
})

export const addVehicle = (
  request: StandardServiceCall,
  vehicle: PartialVehicle & { unattended?: boolean; unattendedNotes?: string },
  vehicleCondition?: string,
) => {
  const { color, year, make, model, vin, licensePlate, licensePlateState, unattended = false, unattendedNotes } = vehicle
  return {
    ...request,
    vehicle: {
      ...request.vehicle,
      // AAA dispatch has a max length for this color field
      color: isNil(color) || isEmpty(color) ? 'unknown' : color.length > 8 ? color.substring(0, 8) : color,
      year,
      make,
      model,
      vin,
      tag: licensePlate,
      state: licensePlateState,
      unattended,
      unattendedNotes,
      condition: vehicleCondition,
    },
  }
}

export const transformExtendedAddress = (address: GeocodeAddress): Partial<AaaAddress> => ({
  streetNumber: address.HouseNumber,
  street: address.Street,
  city: address.City,
  postalCode: address.PostalCode,
  state: address.State,
  country: address.Country,
})

export const pruneAAADispatch = (aaaDispatch: StandardServiceCall): PrunedAAADispatch => {
  const prunedDispatch = { ...aaaDispatch } as PrunedAAADispatch
  delete prunedDispatch['customer']['contact']
  return prunedDispatch
}

const TowExchangeProgramCodes = {
  [Brands.ALAMO]: '932',
  [Brands.ENTERPRISE]: '930',
  [Brands.NATIONAL]: '931',
}

const TowExchangeProgramNames = {
  [Brands.ALAMO]: 'ALAMO TOW EXCHANGE',
  [Brands.ENTERPRISE]: 'ENTERPRISE TOW EXCHANGE',
  [Brands.NATIONAL]: 'NATIONAL TOW EXCHANGE',
}
